/** Shapes mirroring the FastAPI contracts (camelCase over the wire). */

export interface Meta {
  requestId: string | null
  timestamp: string
}

export interface ApiResponse<T> {
  success: true
  data: T
  message: string | null
  meta: Meta
}

export interface PageInfo {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

export interface PaginatedResponse<T> {
  success: true
  data: T[]
  pageInfo: PageInfo
  meta: Meta
}

export interface ErrorDetail {
  field: string | null
  message: string
  code: string | null
}

/** RFC 7807 problem document. */
export interface ProblemDetail {
  type: string
  title: string
  status: number
  detail: string | null
  instance: string | null
  code: string
  errors: ErrorDetail[]
  requestId: string | null
  timestamp: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export type UserRole = 'job_seeker' | 'admin'
export type UserStatus = 'pending_verification' | 'active' | 'suspended' | 'deactivated'

export interface User {
  id: string
  email: string
  fullName: string | null
  role: UserRole
  status: UserStatus
  emailVerified: boolean
  creditsRemaining: number
  createdAt: string
  lastLoginAt: string | null
}

export interface TokenPair {
  accessToken: string
  refreshToken: string
  tokenType: string
  expiresAt: string
  expiresIn: number
}

export interface OnboardingSummary {
  isComplete: boolean
  currentStep: OnboardingStepKey
  currentIndex: number
  totalSteps: number
  completedSteps: string[]
  percentComplete: number
  resumeStatus: ResumeStatus
  resumeProgress: number
}

export interface AuthSession {
  user: User
  tokens: TokenPair
  onboarding: OnboardingSummary
}

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------
export type OnboardingStepKey =
  | 'location'
  | 'contact'
  | 'work_eligibility'
  | 'checklist'
  | 'application_password'
  | 'application_settings'

export interface StepDefinition {
  key: OnboardingStepKey
  index: number
  eyebrow: string
  title: string
  subtitle: string
  isOptional: boolean
}

export type ResumeStatus = 'missing' | 'uploaded' | 'parsing' | 'parsed' | 'failed'

export interface OnboardingState {
  currentStep: OnboardingStepKey
  currentIndex: number
  totalSteps: number
  completedSteps: OnboardingStepKey[]
  skippedSteps: string[]
  isComplete: boolean
  completedAt: string | null
  percentComplete: number
  resumeStatus: ResumeStatus
  resumeProgress: number
  draftPayload: Record<string, unknown>
  steps: StepDefinition[]
}

export interface PasswordStrength {
  minLength: boolean
  lowercase: boolean
  uppercase: boolean
  digit: boolean
  special: boolean
  isValid: boolean
}

export interface GeneratedPassword {
  password: string
  strength: PasswordStrength
}

export type ResumeOptimization = 'off' | 'honest' | 'aggressive'
/** How questions the profile cannot answer are handled: asked of you, or answered favourably. */
export type AnswerMode = 'honest' | 'aggressive'

export interface ApplicationSettings {
  resumeOptimization: ResumeOptimization
  answerMode: AnswerMode
  autoApproveEdits: boolean
  generateCoverLetter: boolean
  coverLetterTemplate: string | null
  reviewBeforeSubmit: boolean
  autoApplyEnabled: boolean
  dailyApplicationLimit: number
  minMatchScore: number
  hasApplicationPassword: boolean
  applicationEmailMode: ApplicationEmailMode
  /** The address applications go out with under the current choice. */
  applicationEmail: string | null
  /** The tag of the "+jobs" address, for the option's label. */
  applicationEmailPlusTag: string
  /** False until the server has a mail domain: the alias option is disabled. */
  aliasAvailable: boolean
  /** The deployment applies with the private address only: shown, not a choice. */
  applicationEmailLocked?: boolean
  /** When Auto Apply may run (Power plan); null = any time. */
  autoApplySchedule?: AutoApplySchedule | null
  /** The filters background Auto Apply follows; null = every match. Omitted on save keeps the stored value. */
  autoApplyFilters?: AutoApplyFilters | null
  updatedAt: string | null
}

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

/** The days and hours Auto Apply runs, in the user's own time zone. */
export interface AutoApplySchedule {
  days: Weekday[]
  /** 'HH:MM', 24-hour. */
  start: string
  end: string
  /** IANA name, e.g. 'Asia/Kolkata'. */
  timezone: string
}

/** Which address employers get: the Gmail as-is, a +tag address, or the alias. */
export type ApplicationEmailMode = 'plain' | 'plus_tag' | 'alias'

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------
export type TernaryAnswer = 'yes' | 'no' | 'prefer_not_to_say'
export type AuthorizationBasis =
  | 'citizen'
  | 'permanent_resident'
  | 'work_visa'
  | 'student_visa'
  | 'dependent_visa'
  | 'work_permit'
  | 'other'

export interface LocationPayload {
  addressLine1?: string | null
  addressLine2?: string | null
  city: string
  postalCode?: string | null
  countyDistrict?: string | null
  countryCode: string
  countryName: string
  stateCode?: string | null
  stateName?: string | null
  latitude?: number | null
  longitude?: number | null
  locationLabel?: string | null
}

export interface ContactPayload {
  phoneNumber?: string | null
  linkedinUrl?: string | null
  githubUrl?: string | null
  portfolioUrl?: string | null
}

export interface WorkAuthorizationPayload {
  countryCode: string
  countryName: string
  legallyAuthorized?: boolean | null
  requiresSponsorship?: boolean | null
  authorizationBasis?: AuthorizationBasis | null
  visaExpiresOn?: string | null
  notes?: string | null
}

export interface WorkEligibilityPayload {
  citizenshipCountryCodes: string[]
  workAuthorizations: WorkAuthorizationPayload[]
}

export interface ChecklistPayload {
  openToInPerson: boolean
  willingToRelocate: boolean
  canStartImmediately: boolean
  hasReliableTransportation: boolean
  needsAccommodations: TernaryAnswer
  accommodationDetails?: string | null
  hasGovernmentClearance: boolean
  clearanceDetails?: string | null
  hasForeignGovernmentTies: boolean
  gender?: string | null
  raceEthnicity?: string | null
  veteranStatus: TernaryAnswer
  disabilityStatus: TernaryAnswer
  additionalNotes?: string | null
}

export interface Skill {
  id: string
  name: string
  category: string | null
  yearsOfExperience: number | null
  isPrimary: boolean
}

export interface JobPreference {
  desiredTitles: string[]
  desiredLocations: string[]
  excludedKeywords: string[]
  excludedCompanies: string[]
  targetCompanies: string[]
  industries: string[]
  workplaceTypes: string[]
  employmentTypes: string[]
  minSalary: number | null
  maxSalary: number | null
  salaryCurrency: string
  minExperienceYears: number | null
  maxExperienceYears: number | null
  requiresVisaSponsorship: boolean
  openToRemoteWorldwide: boolean
  /** How many jobs a profile search keeps from company career pages. */
  matchLimit: number | null
}

/** One role on the profile, from the resume parser or typed in. */
export interface ProfileExperience {
  id: string
  company: string
  title: string
  location: string | null
  /** "YYYY-MM", or a bare "YYYY" when the resume only gave a year. */
  startDate: string | null
  /** "YYYY-MM", or a bare "YYYY". */
  endDate: string | null
  isCurrent: boolean
  description: string | null
  source: 'resume' | 'user'
}

export interface ProfileExperiencePayload {
  company: string
  title: string
  location?: string | null
  startDate?: string | null
  endDate?: string | null
  isCurrent?: boolean
  description?: string | null
}

/** What the AI rewrite is asked to polish: a role's bullets, the summary, or the headline. */
export type ProfileEnhanceKind = 'experience' | 'summary' | 'headline'

export interface ProfileEnhanceRequest {
  kind: ProfileEnhanceKind
  /** What the user wrote; for a role, bullets one per line. */
  text: string
  company?: string | null
  title?: string | null
  isCurrent?: boolean | null
  /** The profile's skill names, so the rewrite keeps their spelling. */
  skills?: string[]
  totalExperienceYears?: number | null
}

/** Nothing is saved: the client puts `text` into the field and the user saves as usual. */
export interface ProfileEnhanceResult {
  text: string
  /** False when the text came back as it was; `reason` says why. */
  changed: boolean
  reason: string | null
}

export interface Profile {
  id: string
  userId: string
  firstName: string | null
  lastName: string | null
  fullName: string | null
  headline: string | null
  summary: string | null
  currentTitle: string | null
  currentCompany: string | null
  totalExperienceYears: number | null
  noticePeriodDays: number | null
  currentSalary: number | null
  expectedSalary: number | null
  salaryCurrency: string | null
  /** Education, as employer forms ask for it. */
  educationUniversity: string | null
  educationCourse: string | null
  educationGraduationYear: number | null
  /** `YYYY-MM-DD`; typed only where a portal or employer form requires it. */
  dateOfBirth: string | null
  location: LocationPayload | null
  contact: ContactPayload | null
  workEligibility: {
    citizenshipCountryCodes: string[]
    workAuthorizations: (WorkAuthorizationPayload & {
      id: string
      isCitizenship: boolean
      isWorkTarget: boolean
    })[]
  } | null
  checklist: ChecklistPayload | null
  jobPreference: JobPreference | null
  skills: Skill[]
  experiences: ProfileExperience[]
  createdAt: string
  updatedAt: string
}

export type ProfileRecommendationField =
  | 'current_salary'
  | 'expected_salary'
  | 'ctc'
  | 'notice_period'
  | 'skill_years'
  | 'additional_notes'
  | 'current_title'
  | 'current_company'
  | 'linkedin'
  | 'skills'

/** Something to add to the profile so future screening questions answer themselves. */
export interface ProfileRecommendation {
  id: string
  title: string
  detail: string
  field: ProfileRecommendationField
  skill: string | null
  askedCount: number
  examples: string[]
}

// ---------------------------------------------------------------------------
// Resumes
// ---------------------------------------------------------------------------
export interface Resume {
  id: string
  fileName: string
  contentType: string
  sizeBytes: number
  status: ResumeStatus
  isDefault: boolean
  label: string | null
  documentId: string | null
  parseError: string | null
  parsedAt: string | null
  createdAt: string
}

export interface ParsedResume {
  documentId: string
  resumeId: string
  userId: string
  fullName: string | null
  email: string | null
  phone: string | null
  headline: string | null
  summary: string | null
  totalExperienceYears: number | null
  skills: string[]
  experiences: Array<Record<string, unknown>>
  education: Array<Record<string, unknown>>
}

export interface ResumeParseResult {
  resume: Resume
  parsed: ParsedResume | null
  appliedToProfile: boolean
  /** What `applyToProfile` wrote: identity fields, roles, education, skills. */
  applied: { fields: string[]; experiences: number; education: boolean; skills: number } | null
}

// ---------------------------------------------------------------------------
// Integrations
// ---------------------------------------------------------------------------
export type IntegrationProvider = 'naukri' | 'linkedin'
export type IntegrationStatus =
  | 'disconnected'
  | 'pending_verification'
  | 'connected'
  | 'error'

export interface Integration {
  id: string
  provider: IntegrationProvider
  status: IntegrationStatus
  authMethod: 'password' | 'mobile_otp' | null
  accountIdentifier: string | null
  isConnected: boolean
  /** Connected under the other portal driver; applying needs a fresh sign-in. */
  needsReconnect: boolean
  jobsSynced: number
  lastSyncedAt: string | null
  lastError: string | null
  createdAt: string
}

export interface OtpChallenge {
  challengeId: string
  mobileNumber: string
  expiresAt: string
  attemptsRemaining: number
}

export interface IntegrationSync {
  provider: IntegrationProvider
  jobsFound: number
  jobsImported: number
  syncedAt: string
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------
export type JobSource =
  | 'naukri'
  | 'linkedin'
  | 'greenhouse'
  | 'lever'
  | 'workday'
  | 'icims'
  | 'oracle'
  | 'manual'
  /** Picked for this user from our pool of employer career pages. */
  | 'internal'

export type MatchTier = 'strong' | 'good' | 'fair' | 'weak'

/** The job portal a job was pulled from (a `JobPortal` key and its name). */
export interface JobBoardRef {
  key: string
  name: string
}

export interface Job {
  id: string
  source: JobSource
  /** Set when the job came from one of the user's job portals. */
  board?: JobBoardRef | null
  externalId: string
  applyUrl: string
  title: string
  companyName: string
  company: { id: string | null; name: string; logoUrl: string | null; industry: string | null } | null
  location: string | null
  city: string | null
  countryCode: string | null
  workplaceType: string | null
  employmentType: string | null
  minExperienceYears: number | null
  maxExperienceYears: number | null
  minSalary: number | null
  maxSalary: number | null
  salaryCurrency: string | null
  degreeLevel: string | null
  sponsorsVisa: boolean | null
  easyApply: boolean
  skills: string[]
  tags: string[]
  postedAt: string | null
  expiresAt: string | null
  isActive: boolean
  description: string | null
  /** The board's reasons, e.g. ["Profile", "You might like"]. */
  recommendedFor: string[]
  /** The board says applying opens a screening questionnaire. */
  hasQuestionnaire: boolean | null
}

export interface ScoreBreakdown {
  fitPenalty?: number

  skills: number
  title: number
  experience: number
  /** How well the job description reads against the profile and resume. */
  description: number
  location: number
  salary: number
  workAuthorization: number
}

export interface JobMatch {
  id: string
  job: Job
  matchScore: number
  matchTier: MatchTier
  scoreBreakdown: ScoreBreakdown
  matchedSkills: string[]
  missingSkills: string[]
  isSaved: boolean
  isPassed: boolean
  viewedAt: string | null
  hasApplied: boolean
}

/** One "pull jobs from" choice on the dashboard. */
export interface JobSourceOption {
  source: JobSource
  /** A job portal's key. Such entries have `source: 'manual'` and the portal's name as `label`. */
  board?: string | null
  label: string
  available: boolean
  connected: boolean
  jobCount: number
  lastSyncedAt: string | null
  applyModes: ApplyMode[]
  /** Company careers only: the first match against the career pages is running now. */
  matching?: boolean
  /** Company careers only: why the career pages cannot be matched yet. */
  note?: string | null
}

// ---------------------------------------------------------------------------
// Job portals (Profile → Job Portals)
// ---------------------------------------------------------------------------
export type JobPortalStatus = 'not_connected' | 'connecting' | 'needs_you' | 'connected' | 'error'

/** A job board the product can use for the user: account, profile, feed, apply. */
/** `POST /integrations/naukri/create-account`: the sign-up runs in the background. */
export interface NaukriCreateAccount {
  status: 'creating'
  accountIdentifier: string | null
  /** Where a step only the person can do (a code texted to their phone) is finished. */
  handoffPath: string
  message: string
}

export interface JobPortal {
  /** 'indeed', 'reed_uk', … or 'custom-<slug>' for one the user added. */
  key: string
  name: string
  url: string
  /** 'US' | 'UK' | 'Global' */
  region: string | null
  /** `feed` = open listings, no account needed. */
  kind: 'feed' | 'site'
  builtIn: boolean
  needsAccount: boolean
  canApplyOnPortal: boolean
  /** Only the account is made here (Naukri); its jobs and applies come through its own connection. */
  accountOnly?: boolean
  /** e.g. "Paid membership needed". */
  note: string | null
  selected: boolean
  fetchJobs: boolean
  applyOnPortal: boolean
  status: JobPortalStatus
  accountIdentifier: string | null
  lastSyncedAt: string | null
  lastError: string | null
  jobCount: number
  script: { fetch: 'ready' | 'none'; account: 'ready' | 'none'; learnedAt: string | null }
  /** When `status` is `needs_you`: a page where the user finishes a step by hand. */
  handoffUrl: string | null
  /** How far the portal profile got on the last signed-in run; `partial` means connecting again finishes it. */
  accountProfile?: 'complete' | 'partial' | null
  /** The user gave their own login for this portal; the password is never returned. */
  hasOwnCredentials?: boolean
  /** The last test run (nothing sent), in short. */
  lastRehearsal?: { at: string; stopReason: string | null; humanCheck: string | null; screens: number } | null
  /** Names of the account set-up screenshots, oldest first (`/job-portals/:key/screenshots/:name`). */
  screenshots?: string[]
}

export interface JobPortalUpdate {
  selected?: boolean
  fetchJobs?: boolean
  applyOnPortal?: boolean
}

export interface JobPortalSync {
  portal: JobPortal
  jobsFound: number
  jobsImported: number
  /** True when the fetch runs in the background. */
  queued: boolean
}

/** What "Find jobs for me" did — `POST /jobs/profile-search`. */
export interface ProfileSearchResult {
  requested: number
  /** Career-page jobs now in the feed. */
  matched: number
  /** Open postings that fit the filters and were relevant, before the cut. */
  eligible: number
  shortlisted: number
  removed: number
  strong: number
  good: number
  fair: number
  weak: number
  /** Jobs from the other sources (Naukri, pasted links) re-scored alongside. */
  rescored: number
  criteria: {
    titles: string[]
    skills: string[]
    locations: string[]
    countries: string[]
    workplaceTypes: string[]
    employmentTypes: string[]
    experienceYears: number | null
  }
  tookMs: number
}

export interface JobFacet {
  value: string
  label: string
  count: number
}

export interface JobFacets {
  sources: JobFacet[]
  locations: JobFacet[]
  companies: JobFacet[]
  workplaceTypes: JobFacet[]
  employmentTypes: JobFacet[]
}

/** One choice in a filter's search box. */
export interface JobFilterOption {
  /** What goes back in the filter: a company name, a city, or `country:IN`. */
  value: string
  label: string
  kind: 'country' | 'state' | 'city' | 'company'
  count: number
}

/** The fixed choices of the filters that read a job's text. */
export interface JobFilterChoices {
  regions?: Array<{ value: string; label: string }>

  roles: Array<{ value: string; label: string }>
  seniorities: Array<{ value: string; label: string }>
  degreeLevels: Array<{ value: string; label: string }>
}

export interface FetchedJobDescription {
  title: string | null
  companyName: string | null
  location: string | null
  description: string | null
  source: JobSource
  applyUrl: string
}

// ---------------------------------------------------------------------------
// Applications
// ---------------------------------------------------------------------------
export type ApplicationStatus =
  | 'draft'
  | 'queued'
  | 'in_flight'
  | 'needs_you'
  | 'submitted'
  | 'failed'
  | 'skipped'
  | 'job_expired'

export type ApplicationOutcome =
  | 'applied'
  | 'viewed'
  | 'screening'
  | 'interview'
  | 'offer'
  | 'rejected'
  | 'withdrawn'

export type ScreeningQuestionType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'single_select'
  | 'multi_select'
  | 'date'
  | 'file'

/** Who supplied an answer — shown beside it on the review screen. */
export type AnswerSource = 'profile' | 'previous_answer' | 'ai' | 'user'

/** Board quick-apply, or the employer's own careers site. */
export type ApplyMode = 'easy_apply' | 'company_portal'

export interface ScreeningQuestion {
  id: string
  questionKey: string
  questionText: string
  questionType: ScreeningQuestionType
  isRequired: boolean
  answerValue: string | null
  options: string[]
  wasAutofilled: boolean
  confidence: number | null
  answerSource: AnswerSource | null
  rationale: string | null
  /** A low-confidence AI draft; offered, never submitted as-is. */
  suggestedValue: string | null
  /** An actionable profile tip, e.g. "Add your current CTC to your profile". Absent on older APIs. */
  tip?: string | null
  /** `false` when the portal's list is a part of the whole: a value not listed may be typed. */
  optionsComplete?: boolean
  /** For a `file` question: which document the slot takes. */
  documentKind?: DocumentKind
}

export type AnswerAssistMode = 'draft' | 'improve'

// ---------------------------------------------------------------------------
// Review inbox: every waiting application's open questions in one place
// ---------------------------------------------------------------------------
export type WaitingFor = 'answers' | 'ready' | 'review' | 'verification' | 'captcha' | 'rehearsal' | 'auto' | 'other'

export interface ReviewAskedBy {
  siteError?: string | null
  valueTried?: string | null
  applicationId: string
  questionKey: string
  company: string
  jobTitle: string
  options: string[]
  /** `false` when this portal's list is a part of the whole: a value not listed may be typed. */
  optionsComplete?: boolean
  isRequired: boolean
}

/** The inbox heading each `QuestionLevel` sits under (the type is defined beside `ScreenState`). */
export const LEVEL_LABELS: Record<QuestionLevel, string> = {
  profile: 'About you',
  experience: 'Your experience',
  education: 'Your education',
  skills: 'Your skills',
  application: 'This application',
  account: 'Account',
  document: 'Documents',
  consent: 'Consents',
  declaration: 'Declarations',
}

/** A paper a form asks for besides the résumé, uploaded once under Profile → Documents. */
export type DocumentKind = 'photo' | 'id_proof' | 'passport' | 'pan' | 'certificate' | 'marksheet' | 'payslip' | 'offer_letter' | 'relieving_letter' | 'other'

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  photo: 'Photo',
  id_proof: 'ID proof',
  passport: 'Passport',
  pan: 'PAN card',
  certificate: 'Certificate',
  marksheet: 'Mark sheet',
  payslip: 'Pay slip',
  offer_letter: 'Offer letter',
  relieving_letter: 'Relieving letter',
  other: 'Document',
}

export interface UserDocument {
  id: string
  kind: DocumentKind
  fileName: string
  mimeType: string
  sizeBytes: number
  createdAt: string
}

export interface ReviewQuestion {
  siteError?: string | null
  valueTried?: string | null
  /** Which part of the candidate it is about (`LEVEL_LABELS`). Absent on older APIs. */
  level?: QuestionLevel
  /** For a `file` question: which document the slot takes. */
  documentKind?: DocumentKind
  groupKey: string
  questionText: string
  questionType: ScreeningQuestionType
  options: string[]
  /** `false` when any portal's list is a part of the whole: a value not listed may be typed. */
  optionsComplete?: boolean
  isRequired: boolean
  suggestedValue: string | null
  previousAnswer: string | null
  rationale: string | null
  tip: string | null
  askedBy: ReviewAskedBy[]
}

export interface ReviewApplication {
  id: string
  company: string
  jobTitle: string
  status: ApplicationStatus
  waitingFor: WaitingFor
  reason: string | null
  openRequired: number
  openOptional: number
  externalApplyUrl: string | null
  lastStatusAt: string | null
  /** `auto` only: what the policy parked it for, and until when it waits before looking again. */
  waitingKind?: 'mail' | 'time' | 'build' | null
  waitingUntil?: string | null
  /** A hand-off: apply on the employer's site (a human check no browser of ours passes). */
  handoff?: boolean
  /** The run's page is still open on a worker: "Save & submit" continues it on that screen. */
  live?: { parked: boolean; since?: string | null; workerId?: string | null } | null
  /** The screen the run stopped on: the real blocker (a CAPTCHA, questions, a document…) and what to do. */
  screen?: InboxScreen | null
}

export interface RetryAllApplication {
  applicationId: string
  company: string | null
  jobTitle: string | null
}

export interface RetryAllResult {
  counts: { queued: number; skippedUnconfirmed: number; skipped: number }
  queued: RetryAllApplication[]
  skippedUnconfirmed: RetryAllApplication[]
  skipped: RetryAllApplication[]
}

export interface ReviewInbox {
  summary: { waiting: number; needsAnswers: number; needsReview: number; ready: number; openQuestions: number }
  questions: ReviewQuestion[]
  applications: ReviewApplication[]
}

export interface SubmitReviewAnswersResult {
  answered: number
  queued: string[]
  retried: string[]
  stillOpen: Array<{ applicationId: string; waitingFor: WaitingFor; openRequired: number }>
  unmatched: Array<{ groupKey: string; applicationId: string; options: string[] }>
}

/** One kept answer of the answer bank (`profile_answers`). */
export interface BankEntry {
  questionKey: string
  questionText: string
  questionType: string
  options: string[]
  answerValue: string
  answerSource: string
  timesUsed: number
  updatedAt: string
}

export interface ApplicationEvent {
  id: string
  eventType: string
  message: string | null
  fromStatus: string | null
  toStatus: string | null
  createdAt: string
  /** Event details: an agent turn's diagnosis/actions, rejected keys, screenshot names. */
  payload?: Record<string, unknown> | null
}

/** Where an application is sent, and where its job was found. */
export interface AppliedVia {
  /** `portal`: the job portal takes the application. `company_site`: the employer's own site. */
  channel: 'portal' | 'company_site'
  portalKey: string | null
  /** "Naukri", "foundit (Monster India)", "Company site (Workday)". */
  label: string
  foundOn: { key: string; label: string }
}

/** One source of jobs and what became of it (`GET /job-portals/summary`). */
export interface JobSourceSummary {
  key: string
  label: string
  kind: 'portal' | 'direct'
  matches: number
  applications: number
  appliedOnPortal: number
  appliedOnCompanySite: number
  submitted: number
  needsYou: number
  inProgress: number
}

export interface Application {
  id: string
  job: Job
  /** Absent on older APIs: the tracker then falls back to the apply mode. */
  appliedVia?: AppliedVia
  status: ApplicationStatus
  outcome: ApplicationOutcome | null
  applyMode: ApplyMode | null
  externalApplyUrl: string | null
  resumeVariant: 'default' | 'optimized' | 'skipped'
  matchScore: number | null
  isAutoApplied: boolean
  requiresReview: boolean
  submittedAt: string | null
  failureReason: string | null
  externalReference: string | null
  notes: string | null
  /** "Needs you" with every required detail filled — only the go-ahead is left. */
  readyToSubmit: boolean
  /** "Needs you" because a run stopped: `failureReason` says why, and Retry runs it again. No questions. */
  needsReview: boolean
  /** When "Retry all" last put it back on the queue: a queued run after that is a retry. Absent on older APIs. */
  retriedAt?: string | null
  /** The run waits for you on its live page right now (a CAPTCHA, a code): yours to act on. Absent on older APIs. */
  waitingForYou?: { reason: string; waitedSeconds: number | null } | null
  createdAt: string
  updatedAt: string
}

export interface ApplicationDetail extends Application {
  events: ApplicationEvent[]
  screeningQuestions: ScreeningQuestion[]
  pendingRequiredCount: number
  resumeId: string | null
  resumeFileName: string | null
  scoreBreakdown: ScoreBreakdown | null
  matchedSkills: string[]
  missingSkills: string[]
  /** Absent until the backend ships tailoring; read with `?.` and a 'none' fallback. */
  tailoredResume?: TailoredResume
  coverLetter?: CoverLetter
  /** The full posting, plain text or HTML. */
  jobDescription?: string | null
  /** Which applicant-tracking system the employer uses ('workday', 'greenhouse', …). */
  portalType?: string | null
  /** The address the portal account was created with (usually the mail alias). */
  portalAccountEmail?: string | null
  /** Mail received about this application, newest first. */
  emails?: ApplicationEmail[]
  /** The current (or last) run is a test run: nothing is submitted. */
  isRehearsal?: boolean
  /** A test-run report can be fetched (`applicationApi.rehearsal`). */
  hasRehearsalReport?: boolean
  /** An agent took part in a run: { mode, turns, patchReused, outcome, … }. */
  agentMeta?: AgentMeta | null
  /** An agent trace can be fetched (`applicationApi.agentTrace`). */
  hasAgentTrace?: boolean
  /** Where each run ended, oldest first (`applicationApi.screenshot`). */
  screenshots?: ApplicationScreenshot[]
  /** Every field the run filled on the employer's form, by section; null until a screen was filled. */
  appliedForm?: AppliedForm | null
  /** The screen the last run stopped on: what blocked it, what was tried, what is missing by level. */
  screen?: ScreenState | null
}

/** Where a question belongs (`ScreenMissing.level`). */
export type QuestionLevel = 'profile' | 'experience' | 'education' | 'skills' | 'application' | 'account' | 'document' | 'consent' | 'declaration'

export type ScreenBlockerKind =
  | 'captcha'
  | 'captcha_typed_refused'
  | 'questions'
  | 'document'
  | 'account'
  | 'verify_mail'
  | 'site_refused'
  | 'fill_failed'
  | 'site_stuck'
  | 'submit'
  | 'crash'
  | 'posting_gone'
  | 'other'

export type ScreenBlockerNeeds = 'solve_live' | 'answers' | 'upload' | 'retry' | 'code_fix' | 'wait' | 'nothing'

export interface ScreenBlocker {
  kind: ScreenBlockerKind
  needs: ScreenBlockerNeeds
  /** One plain sentence for the person. */
  message: string
  element?: { key: string; label: string | null; inputKind: string | null }
  /** The site's own error text. */
  siteWords?: string
  /** What was tried before stopping ("typed pw324e (refused)", "fleet solver: not found"). */
  tried?: string[]
}

export interface ScreenMissing {
  key: string
  label: string
  level: QuestionLevel
  required: boolean
}

/** The screen a run stopped on (`form_snapshot.screen`). */
export interface ScreenState {
  v: 1
  at: string
  url: string | null
  host: string | null
  portal: string | null
  step: string | null
  stepIndex: number | null
  phase: string | null
  /** The evidence file of the screen as the run left it (`applicationApi.screenshot`). */
  screenshot: string | null
  blocker: ScreenBlocker
  counts: { fields: number; filled: number; required: number; missingRequired: number; optionalEmpty: number }
  missing: ScreenMissing[]
  documents: Array<{ label: string; kind: string; required: boolean }>
  /** The page is still open on a worker and waits for the person. */
  live: { parked: boolean; workerId?: string | null; since?: string | null; expiresAt?: string | null } | null
}

/** The screen as the inbox carries it per application. */
export interface InboxScreen {
  step: string | null
  blocker: Pick<ScreenBlocker, 'kind' | 'needs' | 'message'>
  missingRequired: number
  screenshot: string | null
  parked: boolean
}

/** The sections of the applied form, in reading order. */
export type AppliedSectionId =
  | 'personal'
  | 'experience'
  | 'education'
  | 'skills'
  | 'documents'
  | 'questions'
  | 'voluntary'
  | 'agreements'
  | 'other'

/** Who supplied a field's value on the employer's form. */
export type AppliedSource = 'profile' | 'resume' | 'previous_answer' | 'ai' | 'user' | 'fallback' | 'form' | null

/** One field the run filled on the employer's form. */
export interface AppliedField {
  key: string
  label: string
  type: string
  required: boolean
  /** The first options (up to 12); `optionsCount` is the real length. */
  options: string[]
  optionsCount: number
  value: string | null
  source: AppliedSource
  rationale: string | null
  /** Answered to keep the form moving (a consent, a decline, a best-effort guess), not a fact from the profile. */
  fallback: boolean
  fillError: string | null
  step: string
  index: number
  section: AppliedSectionId
  /** The employer or school, when the field belongs to one work or education entry. */
  entry: string | null
}

export interface AppliedStep {
  index: number
  step: string
  fields: number
  ms: number | null
  at: string
}

export interface AppliedSection {
  id: AppliedSectionId
  title: string
  fields: AppliedField[]
}

/** Everything the run put on the employer's form, by section (`ApplicationDetail.appliedForm`). */
export interface AppliedForm {
  v: 1
  sections: AppliedSection[]
  steps: AppliedStep[]
  stats: { fields: number; answered: number; fallbacks: number }
  updatedAt: string
}

/** A picture of where one run ended: the confirmation page, or where it stopped. */
export interface ApplicationScreenshot {
  name: string
  stage?: string | null
  capturedAt?: string | null
}

/** A run parked for the user: a CAPTCHA to solve on the live page. */
export interface LiveCheckpoint {
  waiting: boolean
  /** `captcha`, `human`, `sms` (the click relay works) or `parked` (a page kept open for the person's answers; picture only). */
  reason?: string | null
  url?: string | null
  width?: number | null
  height?: number | null
  waitedSeconds?: number | null
}

/** One of the user's runs waiting on them right now (the app-wide banner). */
export interface LiveWaiting {
  applicationId: string
  jobTitle?: string | null
  companyName?: string | null
  reason?: string | null
  waitedSeconds?: number | null
}

/** The agent's compact run record kept on the application. */
export interface AgentMeta {
  mode: 'repair' | 'navigator'
  turns: number
  patchReused: boolean
  outcome: string
  lastSignature: string | null
}

/** One agent move as the trace shows it: the target, never the value. */
export interface AgentActionSummary {
  kind: string
  target: string | null
  purpose: string | null
  reason: string | null
}

/** One screen the agent worked on. */
export interface AgentTurn {
  index: number
  title: string
  url: string | null
  phase: string
  diagnosis: string
  actions: AgentActionSummary[]
  actionErrors: string[]
  signature: string
  patchReused: boolean
  /** File names inside the trace; fetch with `applicationApi.agentTraceFile`. */
  screenshot: string | null
  html: string | null
  screenshotUrl: string | null
  htmlUrl: string | null
}

/** What the repair/navigator agent saw and did on the employer's site. */
export interface AgentTraceReport {
  applicationId: string
  mode: 'repair' | 'navigator'
  portal: string
  startedAt: string | null
  finishedAt: string | null
  turns: AgentTurn[]
  outcome: string
  message: string
}

/** Who supplied a test-run answer: the answer sources plus the connector's own. */
export type RehearsalSource = AnswerSource | 'automatic' | 'resume'

/** One field a test run saw on the employer's form. */
export interface RehearsalField {
  key: string
  label: string
  type: string
  required: boolean
  options: string[]
  answer: string | null
  source: RehearsalSource | null
  /** Why filling it failed on the portal, if it did. */
  fillError: string | null
  /** Left empty on purpose: an account form a test run does not fill. */
  skipped?: boolean
}

export interface RehearsalStep {
  index: number
  title: string
  url: string | null
  /** File names inside the report; fetch with `applicationApi.rehearsalFile`. */
  screenshot: string | null
  html: string | null
  screenshotUrl: string | null
  htmlUrl: string | null
  fields: RehearsalField[]
  errors: string[]
  /** The step the run stopped on. */
  stop: boolean
}

export type RehearsalAccountGate = 'none' | 'create_account' | 'sign_in' | 'verification'

/** What a test run ("rehearsal") saw on the employer's portal. */
export interface RehearsalReport {
  applicationId: string
  portal: string
  portalLabel: string
  startedAt: string | null
  finishedAt: string | null
  account: RehearsalAccountGate
  accountDetail: string
  steps: RehearsalStep[]
  unanswered: Array<{ step: string; key: string; label: string }>
  errors: string[]
  stop: {
    kind: 'submit' | 'create_account' | 'sign_in' | 'verification' | 'errors' | 'failed' | 'naukri'
    step: string
    reason: string
    message: string | null
  } | null
  screenshots: string[]
}

export interface ApplicationEmail {
  id: string
  subject: string
  category: MailCategory
  receivedAt: string
  fromName: string | null
}

export type GeneratedDocStatus = 'none' | 'pending' | 'ready' | 'failed'

export interface TailoredResume {
  status: GeneratedDocStatus
  html: string | null
  fileName: string | null
  error: string | null
}

export interface CoverLetter {
  status: GeneratedDocStatus
  text: string | null
}

/** What the apply dialog asks every time. */
export interface ApplyOptions {
  tailorResume?: boolean
  coverLetter?: boolean
}

export interface ApplicationPosition {
  index: number
  total: number
  previousId: string | null
  nextId: string | null
}

export interface ApplicationStats {
  total: number
  submitted: number
  inFlight: number
  needsYou: number
  failed: number
  skipped: number
  draft: number
  queued: number
  jobExpired: number
}

export interface DashboardSummary {
  stats: ApplicationStats
  creditsRemaining: number
  topMatchesCount: number
  unreadNeedsYou: number
}

export interface BulkApplyResult {
  queued: string[]
  skipped: Array<{ jobId: string; reason: string }>
  totalRequested: number
}

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------
export interface Country {
  code: string
  name: string
  dialCode: string
  flag: string
}

export interface Option {
  value: string
  label: string
  description?: string
}

export interface ReferenceData {
  countries: Country[]
  genderOptions: string[]
  raceEthnicityOptions: string[]
  degreeLevels: string[]
  authorizationBasis: Option[]
  workplaceTypes: Option[]
  employmentTypes: Option[]
  ternaryOptions: Option[]
  resumeOptimizationOptions: Option[]
  answerModeOptions: Option[]
}

// ---------------------------------------------------------------------------
// Application mail (alias inbox + connected mailboxes)
// ---------------------------------------------------------------------------
export type MailCategory =
  | 'verification'
  | 'applied'
  | 'rejection'
  | 'interview'
  | 'assessment'
  | 'reminder'
  | 'offer'
  | 'other'

export type MailSource = 'alias' | 'gmail' | 'outlook' | 'webhook'

export interface MailAlias {
  address: string | null
  enabled: boolean
  forwardingEnabled: boolean
  forwardTo: string | null
  domainConfigured: boolean
  ingestMode: 'off' | 'imap' | 'webhook'
}

export interface MailAliasUpdate {
  enabled?: boolean
  forwardingEnabled?: boolean
  forwardTo?: string | null
}

export interface MailMessage {
  id: string
  fromAddress: string
  fromName: string | null
  toAddress: string | null
  subject: string
  snippet: string | null
  category: MailCategory
  receivedAt: string
  isRead: boolean
  applicationId: string | null
  companyName: string | null
  verificationKind: 'otp' | 'link' | null
  actionTaken: string | null
  source: MailSource
}

export interface MailMessageDetail extends MailMessage {
  html: string | null
  text: string | null
  verificationValue: string | null
  verificationUsedAt: string | null
  forwardedAt: string | null
  application: {
    id: string
    title: string
    companyName: string
    status: string
    outcome: string | null
  } | null
}

export interface MailStats {
  unread: number
  byCategory: Partial<Record<MailCategory, number>>
}

export type MailProvider = 'gmail' | 'outlook'

export interface MailAccount {
  id: string
  provider: MailProvider
  email: string
  status: 'connected' | 'error' | 'revoked'
  lastSyncedAt: string | null
  lastError: string | null
}

// ---------------------------------------------------------------------------
// Pipeline (tracker board)
// ---------------------------------------------------------------------------
export type PipelineColumnKey = 'applied' | 'ghosted' | 'interviewing' | 'rejected' | 'offer'

export interface PipelineItem {
  applicationId: string
  companyName: string
  title: string
  logoUrl: string | null
  appliedAt: string | null
  emailCount: number
  lastEmailAt: string | null
}

export interface PipelineColumn {
  key: PipelineColumnKey
  label: string
  count: number
  items: PipelineItem[]
}

export interface Pipeline {
  columns: PipelineColumn[]
  flows: Array<{ from: string; to: string; count: number }>
}

// ---------------------------------------------------------------------------
// Chat assistant
// ---------------------------------------------------------------------------
export type ChatRole = 'user' | 'assistant'

export type ChatToolStatus = 'started' | 'done' | 'failed'

/** One thing the assistant did while answering ("Checked 12 applications"). */
export interface ChatTool {
  id: string
  name: string
  label: string
  status: ChatToolStatus
  summary?: string
}

export type PendingActionKind =
  | 'apply'
  | 'pass'
  | 'retry'
  | 'cancel'
  | 'submit_ready'
  | 'retry_captcha'
  | 'update_settings'
  | 'sync_naukri'
  | 'find_jobs'
  | 'rescore'

export type PendingActionStatus = 'pending' | 'done' | 'failed' | 'dismissed'

export interface PendingActionItem {
  id: string
  label: string
  sublabel?: string
  score?: number
}

/** Something the assistant wants to do and needs the user's go-ahead for. */
export interface PendingAction {
  id: string
  kind: PendingActionKind
  /** "Apply to 5 jobs" */
  title: string
  /** One sentence. */
  summary: string
  items?: PendingActionItem[]
  status: PendingActionStatus
  /** After done / failed: one sentence. */
  result?: string
  createdAt: string
}

export interface ChatMessage {
  id: string
  role: ChatRole
  /** Markdown-lite: paragraphs, **bold**, lists, inline links. */
  content: string
  createdAt: string
  tools?: Array<Omit<ChatTool, 'status'> & { status: 'done' | 'failed' }>
  actions?: PendingAction[]
}

export interface ChatThreadSummary {
  id: string
  title: string
  updatedAt: string
}

export interface ChatThread extends ChatThreadSummary {
  messages: ChatMessage[]
}

// ---------------------------------------------------------------------------
// Billing and licensing (jobseeker-be src/schemas/billing.ts, src/licensing/plans.ts)
// ---------------------------------------------------------------------------
export type PlanKey = 'starter' | 'pro' | 'power'
export type BillingPeriod = 'monthly' | 'quarterly' | 'annual'

/** What a plan switches on. */
export interface PlanFeatures {
  /** A resume rewritten for each job. Off: the user's own resume goes as it is. */
  resumeTailoring: boolean
  /** A cover letter written for each job. */
  coverLetterTailoring: boolean
  /** One standard cover letter, sent with every application (when per-job letters are off). */
  fixedCoverLetter: boolean
  /** Applications sent without the user pressing Apply (Auto Apply). */
  autoApply: boolean
  /** Auto Apply on chosen days and hours. */
  scheduling: boolean
  /** An email each evening about the day's applications. */
  dailyReport: boolean
}

export interface PlanPrice {
  /** What is charged for the whole period, in cents. */
  amountCents: number
  /** The same, per month, for comparison. */
  perMonthCents: number
}

export interface PlanResponse {
  key: PlanKey
  name: string
  tagline: string
  monthlyApplications: number
  features: PlanFeatures
  highlights: string[]
  prices: Record<BillingPeriod, PlanPrice>
}

export interface PlansResponse {
  currency: string
  periods: Array<{ key: BillingPeriod; months: number; label: string }>
  plans: PlanResponse[]
  /** False until payments are configured: plans are shown but cannot be bought. */
  billingEnabled: boolean
}

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'expired' | 'incomplete'
export type LicenseErrorCode =
  | 'subscription_required'
  | 'subscription_inactive'
  | 'quota_exhausted'
  | 'feature_not_in_plan'

export interface SubscriptionSummary {
  id: string
  plan: PlanKey
  planName: string
  period: BillingPeriod
  status: SubscriptionStatus
  /** `stripe` (paid) or `admin` (granted by an administrator). */
  source: 'stripe' | 'admin'
  startedAt: string
  /** The paid term: what the last payment (or the grant) covers. */
  currentPeriodStart: string
  currentPeriodEnd: string
  /** Cancelled, effective at `currentPeriodEnd` (no renewal). */
  cancelAtPeriodEnd: boolean
  /** When it renews, if it renews. */
  renewsAt: string | null
}

export interface UsageSummary {
  /** Applications allowed this month of the subscription. */
  limit: number
  /** Sent this month. */
  used: number
  /** Queued or being sent right now. */
  inProgress: number
  /** limit − used − inProgress, never below 0. */
  remaining: number
  /** This month's allowance window; the allowance resets at windowEnd. */
  windowStart: string
  windowEnd: string
}

export interface SubscriptionEventResponse {
  type: string
  message: string
  plan: PlanKey | null
  period: BillingPeriod | null
  at: string
}

export interface SubscriptionResponse {
  /** A subscription in force (active, or past due within the retry window). */
  active: boolean
  subscription: SubscriptionSummary | null
  usage: UsageSummary | null
  /** What the plan switches on (all false without one). */
  features: PlanFeatures
  /** May start another application right now. */
  canApply: boolean
  /** Why not, when `canApply` is false. */
  reason: LicenseErrorCode | null
  /** Newest first. */
  history: SubscriptionEventResponse[]
}

export interface CheckoutRequest {
  plan: PlanKey
  period: BillingPeriod
  /** A partner coupon code, for a new subscription only (422 `coupon_new_subscriptions_only` otherwise). */
  couponCode?: string
}

/** `POST /billing/coupon/validate`. */
export type CouponValidation =
  | { valid: true; code: string; discountPct: number; duration: 'once' | 'forever'; message: string }
  | { valid: false; message: string }

export interface CheckoutResponse {
  /** Stripe Checkout page to send the browser to (a new subscription). */
  url: string | null
  /** An existing paid subscription was moved to the new plan/period instead. */
  changed: boolean
  subscription: SubscriptionResponse | null
}

/** Extra fields on a 402 problem body. */
export interface LicenseProblemExtra {
  reason: LicenseErrorCode
  plan: PlanKey | null
  /** The feature that is missing, for `feature_not_in_plan`. */
  feature?: keyof PlanFeatures
  remaining?: number
  windowEnd?: string
}

// ---------------------------------------------------------------------------
// Auto Apply v2 (docs/auto-apply-v2.md): search with filters, apply with filters
// ---------------------------------------------------------------------------
export type WorkplaceType = 'remote' | 'hybrid' | 'onsite'

/** Every field optional; the same shape the search, the apply and the saved settings use. */
export interface AutoApplyFilters {
  /** Title or company free text. */
  q?: string
  companies?: string[]
  excludeCompanies?: string[]
  /** A city or any text, or a whole country as `country:IN`. */
  locations?: string[]
  /** Two-letter country: also remote jobs open there. */
  remoteIn?: string
  workplaceTypes?: WorkplaceType[]
  employmentTypes?: string[]
  roles?: string[]
  seniorities?: string[]
  postedWithinDays?: number
  maxExperienceYears?: number
  sponsorsVisa?: boolean
  /** Default: the user's settings.minMatchScore (else 60). */
  minMatchScore?: number
  /** Origins: naukri, internal (career pages), portal sources. */
  sources?: string[]
  /** Job portal keys. */
  boards?: string[]
}

/** One way to widen the search, and how many more jobs it would find. */
export interface AutoApplySuggestion {
  id: string
  label: string
  detail: string
  change: Partial<AutoApplyFilters>
  remove?: Array<keyof AutoApplyFilters>
  adds: number
}

export interface AutoApplySearchRequest {
  filters: AutoApplyFilters
  page?: number
  pageSize?: number
  /** True when the filters changed: the server runs a career-page pool search first. */
  refresh?: boolean
}

/** `POST /jobs/auto-apply/search`. */
export interface AutoApplySearchResult {
  items: JobMatch[]
  pageInfo: PageInfo
  totals: {
    /** = pageInfo.totalItems: matched, filtered, not applied. What "apply with filters" uses. */
    ready: number
    /** Career-page openings that fit, from the last pool refresh for these filters. */
    poolEligible: number | null
    /** Ready jobs per origin (naukri, internal, portal keys). */
    bySource: Record<string, number>
  }
  plan: {
    canApply: boolean
    remainingThisMonth: number | null
    remainingToday: number | null
    reason: string | null
  }
  refreshed: boolean
  refreshError: string | null
  /** The career-page search is still running on the server; ask again with `refresh: true` shortly. */
  refreshPending?: boolean
  /** Only when few jobs are ready; empty otherwise. */
  suggestions: AutoApplySuggestion[]
}

export interface AutoApplyApplyRequest {
  filters: AutoApplyFilters
  /** An explicit selection (job ids), max 200. */
  jobIds?: string[]
  /** Without `jobIds`: the top N ready jobs (default 50, max 200). */
  maxJobs?: number
}

/** `POST /jobs/auto-apply/apply`. `queued` is a count (older bulk replies list ids). */
export interface AutoApplyApplyResult {
  queued: number | string[]
  skipped: Array<{ jobId: string; reason: string }>
  remainingThisMonth: number | null
}
